import { useEffect, useState } from 'react';
import { AutoGraph } from '@mui/icons-material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PageLayout from 'domains/core/components/PageLayout';
import Select from 'domains/core/components/Select';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import ContactsDashboard from './dashboards/ContactsDashboard';
import CurationsDashboard from './dashboards/CurationsDashboard';
import ExecutiveOverviewDashboard from './dashboards/ExecutiveOverviewDashboard';
import ValueOverviewDashboard from './dashboards/ValueOverviewDashboard';
import PropensityDashboard from './dashboards/PropensityDashboard';
import SMSDashboard from './dashboards/SMSDashboard';
import VoiceDashboard from './dashboards/VoiceDashboard/VoiceDashboard';

enum DashboardLabels {
    EXECUTIVE_OVERVIEW = 'executive overview',
    CONTACTS = 'contacts',
    CURATIONS = 'curations',
    VALUE_OVERVIEW = 'value overview',
    PROPENSITY_SCORING = 'propensity scoring',
    SMS = 'sms',
    VOICE = 'voice',
}

const InsightsPage = () => {
    const [dashboard, setDashboard] = useState<string>('');

    const { data: featureSettings } = useFeatureSettings();
    const { accessControlLayer: accessControlLayerFlag } = useFlags();
    const hasAccessControlLayer = featureSettings?.acl_enabled && accessControlLayerFlag;

    const getDashboardLabel = (dashboard: string) => {
        if (dashboard === DashboardLabels.SMS) return 'text message';
        if (dashboard === DashboardLabels.CURATIONS) return 'email';
        return dashboard;
    };

    // Define the desired dashboard order
    const dashboardOrder = [
        DashboardLabels.EXECUTIVE_OVERVIEW,
        DashboardLabels.CURATIONS, // email
        DashboardLabels.SMS,
        DashboardLabels.VOICE,
        DashboardLabels.CONTACTS,
        DashboardLabels.PROPENSITY_SCORING,
        DashboardLabels.VALUE_OVERVIEW,
    ];

    const dashboardOptions = dashboardOrder
        ?.filter((dashboard: string) => {
            // Only include dashboards that are enabled
            const isEnabled = featureSettings?.enabled_dashboards?.includes(dashboard);
            // Apply access control layer filter for contacts
            const isAccessible = !(dashboard === DashboardLabels.CONTACTS && hasAccessControlLayer);
            return isEnabled && isAccessible;
        })
        ?.map((dashboard: string) => ({
            label: getDashboardLabel(dashboard),
            value: dashboard,
        }));

    useEffect(() => {
        if (dashboardOptions?.length && !dashboard) {
            setDashboard(dashboardOptions[0].label);
        }
    }, [dashboard, dashboardOptions, featureSettings]);

    const renderNativeDashboard = () => {
        if (dashboard === DashboardLabels.CONTACTS && !hasAccessControlLayer) return <ContactsDashboard />;
        if (dashboard === DashboardLabels.CURATIONS) return <CurationsDashboard />;
        if (dashboard === DashboardLabels.EXECUTIVE_OVERVIEW) return <ExecutiveOverviewDashboard />;
        if (dashboard === DashboardLabels.VALUE_OVERVIEW) return <ValueOverviewDashboard />;
        if (dashboard === DashboardLabels.PROPENSITY_SCORING) return <PropensityDashboard />;
        if (dashboard === DashboardLabels.SMS) return <SMSDashboard />;
        if (dashboard === DashboardLabels.VOICE) return <VoiceDashboard />;
    };

    return (
        <PageLayout
            header="insights"
            headerIcon={<AutoGraph fontSize="inherit" />}
            headerAction={
                <Select
                    fullWidth={false}
                    label="Dashboards"
                    sx={{ textTransform: 'capitalize', width: '12rem' }}
                    value={dashboard}
                    values={dashboardOptions}
                    onChange={(e: any) => setDashboard(e.target.value)}
                />
            }
        >
            {renderNativeDashboard()}
        </PageLayout>
    );
};

export default InsightsPage;
